import { useEffect, useMemo, useState } from 'react';
import { useUser } from '@client/hooks/use-user.hooks';
import { getProject } from '@client/store/project';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { useProjectEvents } from '@client/store/project/project.hook';
import { OPEN_AI_USER_ID } from '@shared/constants';
import { IComment } from '@shared/models/comment.model';
import type { ProjectEvent } from '@shared/models/project-event.model';
import { projectEventChangedAt } from '@shared/helpers/project-events/project-events.helpers';

export const commentSeenKey = (projectId: number): string =>
  `nodon:design-comment:seen:${projectId}`;

const latestOtherCommentAt = (
  comments: readonly IComment[],
  userId: string | undefined,
): string =>
  comments.reduce((latest, comment) => {
    if (!comment.message.trim()) return latest;
    if (comment.owner === userId || comment.owner === OPEN_AI_USER_ID) {
      return latest;
    }
    if (!latest || comment.created_at > latest) return comment.created_at;
    return latest;
  }, '');

/** Agent events belong to the user who asked, so they are never "from others" for them. */
const latestOtherEventAt = (
  events: readonly ProjectEvent[],
  userId: string | undefined,
): string =>
  events.reduce((latest, event) => {
    if (event.actor_id === userId) return latest;
    const changedAt = projectEventChangedAt(event);
    if (!latest || changedAt > latest) return changedAt;
    return latest;
  }, '');

/** True when another user has a comment or change newer than the last time this panel was open. */
export const useDesignCommentsUnread = (isViewing: boolean): boolean => {
  const project = getProject();
  const user = useUser();
  const comments = useCommentsStore((state) => state.comments);
  const events = useProjectEvents();
  const latestOther = useMemo(() => {
    const comment = latestOtherCommentAt(comments, user?.id);
    const event = latestOtherEventAt(events, user?.id);
    return comment > event ? comment : event;
  }, [comments, events, user?.id]);
  const [seen, setSeen] = useState(
    () => localStorage.getItem(commentSeenKey(project.id)) ?? '',
  );

  useEffect(() => {
    setSeen(localStorage.getItem(commentSeenKey(project.id)) ?? '');
  }, [project.id]);

  useEffect(() => {
    if (!isViewing || !latestOther) return;
    localStorage.setItem(commentSeenKey(project.id), latestOther);
    setSeen(latestOther);
  }, [isViewing, latestOther, project.id]);

  return !isViewing && latestOther > seen;
};
